import { MdStop } from "react-icons/md";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";

interface ConversationStatusStripProps {
  /** TTS is playing the bot's reply. Highest priority — also surfaces a stop button. */
  isSpeaking: boolean;
  onStopSpeaking: () => void;
  /** LLM is generating a reply (covers both voice-mode pre-TTS and plain bot typing). */
  isThinking: boolean;
  /** User's mic is open. */
  isListening: boolean;
  /** True once the STT backend has emitted at least one transcript for this session. */
  hasSpeechStarted: boolean;
  /** Verbatim status text from the plan-mode progress slice (e.g. "Génération de
   *  3 images 360°…"). When set, this is the most specific signal we have and
   *  outranks the generic "thinking" label. */
  planMessage?: string | null;
  /** Current scenarioHelper phase. Used to refine the "thinking" label
   *  (refinement turns in ready_to_generate are plan updates, not plain replies). */
  scenarioPhase?: string | null;
  /** A file attachment is still uploading/transcoding/transcribing. */
  hasMediaProcessing: boolean;
}

const DOT_BASE = "inline-block w-2 h-2 rounded-full animate-pulse";

const ConversationStatusStrip = ({
  isSpeaking,
  onStopSpeaking,
  isThinking,
  isListening,
  hasSpeechStarted,
  planMessage,
  scenarioPhase,
  hasMediaProcessing,
}: ConversationStatusStripProps) => {
  const { t } = useTranslation();

  // Priority order picks the most specific signal first so the strip is always
  // the most informative thing we can say. Speaking and plan-progress messages
  // both come from real backend events and are more accurate than the generic
  // "thinking" inference.
  let content: React.ReactNode = null;

  if (isSpeaking) {
    content = (
      <>
        <span aria-hidden="true" className={`${DOT_BASE} bg-green-500`} />
        <span className="text-xs text-gray-500">{t("voice.speakingMessage")}</span>
        <Tooltip title={t("voice.stopSpeaking")} arrow>
          <button
            type="button"
            onClick={onStopSpeaking}
            aria-label={t("voice.stopSpeaking")}
            className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
          >
            <MdStop size={12} />
          </button>
        </Tooltip>
      </>
    );
  } else if (planMessage) {
    // Same pulsing dot as the thinking/listening states below: a plain static
    // line reads as a frozen UI during the (long) consolidation step.
    content = (
      <>
        <span aria-hidden="true" className={`${DOT_BASE} bg-gray-400`} />
        <span className="text-xs text-gray-500 italic">{planMessage}</span>
      </>
    );
  } else if (isThinking && scenarioPhase === "ready_to_generate") {
    // refinePlanThunk runs here without dispatching a planProgress.message,
    // so we infer it from the phase + submitting combo.
    content = (
      <>
        <span aria-hidden="true" className={`${DOT_BASE} bg-gray-400`} />
        <span className="text-xs text-gray-500">{t("voice.updatingPlanMessage")}</span>
      </>
    );
  } else if (isThinking) {
    content = (
      <>
        <span aria-hidden="true" className={`${DOT_BASE} bg-gray-400`} />
        <span className="text-xs text-gray-500">{t("voice.composingReplyMessage")}</span>
      </>
    );
  } else if (isListening && !hasSpeechStarted) {
    content = (
      <>
        <span aria-hidden="true" className={`${DOT_BASE} bg-red-500`} />
        <span className="text-xs text-gray-500">{t("voice.listeningWaitingMessage")}</span>
      </>
    );
  } else if (isListening) {
    content = (
      <>
        <span aria-hidden="true" className={`${DOT_BASE} bg-red-500`} />
        <span className="text-xs text-gray-500">{t("voice.listeningMessage")}</span>
      </>
    );
  } else if (hasMediaProcessing) {
    content = (
      <>
        <span aria-hidden="true" className={`${DOT_BASE} bg-amber-500`} />
        <span className="text-xs text-gray-500">{t("voice.mediaProcessingMessage")}</span>
      </>
    );
  }

  // Always render the row so the input area never shifts vertically.
  return (
    <div className="flex items-center justify-center gap-2 min-h-[20px]" aria-live="polite">
      {content}
    </div>
  );
};

export default ConversationStatusStrip;
